import { InfoOutlined } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import {
  ALLOWED_FILE_EXTENSIONS_PRODUCT,
  ASSET_UPLOAD_SUPPORTED_FILE_TYPES_PRODUCT,
} from 'src/constants';
import { useModal } from 'src/hooks';
import { IAsset, ICCAsset } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import { SimpleDialog } from '../modal';
import { AssetUploadModal } from '../modal/assetUploadModal/AssetUploadModal';
import { VariationImagesTooltip } from '../settings/settingsCategory/SettingsCategory.styled';
import { Snackbar } from '../snackbar';
import {
  IComponentProps,
  IProductVariationAsset,
} from './ProductVariationAssets.interface';
import {
  Container,
  DyHeaderContainer,
  VariationAssetsContainer,
  VariationAssetsHeader,
  VariationAssetsItemWrapper,
} from './ProductVariationAssets.styled';
import { VariationAssetList } from './VariationAssetList';

export const ProductVariationAssets = ({
  variationsAssets,
  themes,
  onChangeVariationAsset,
  onChangeVariationTheme,
  onChangeVariationDY,
  deleteVariationAsset,
  groupProductAsset,
}: IComponentProps) => {
  const { isViewOnly } = useUserRole();
  const assetUploadModal = useModal();
  const replaceAssetModal = useModal();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [uploadedAsset, setUploadedAsset] = useState<File | null>(null);

  // Combine group asset with variation assets, placing group asset first
  const combinedAssets = useMemo(() => {
    // Ensure variationsAssets is an array
    const variationsAssetsArray = Array.isArray(variationsAssets)
      ? variationsAssets
      : [];

    if (!groupProductAsset) {
      return variationsAssetsArray;
    }

    const groupVariationAsset: IProductVariationAsset = {
      assetUrl:
        (groupProductAsset as ICCAsset)?.thumbnailUrl ||
        (groupProductAsset instanceof File
          ? URL.createObjectURL(groupProductAsset)
          : getThumbnailSrc(groupProductAsset.name)),
      assetName: groupProductAsset.name,
      themeIds: [],
      isDY: !variationsAssetsArray.some((asset) => !!asset.isDY),
    };

    return [groupVariationAsset, ...variationsAssetsArray];
  }, [groupProductAsset, variationsAssets]);

  const hasAssets = combinedAssets.length > 0;

  const handleAssetSelection = (file: File) => {
    if (selectedIndex !== null) {
      setUploadedAsset(file);
      replaceAssetModal.onOpenModal();
    } else {
      onChangeVariationAsset(file);
    }
  };

  const filteredSelectedTheme = useMemo(() => {
    if (selectedIndex !== null) {
      return (
        themes
          ?.filter((theme) =>
            combinedAssets[selectedIndex]?.themeIds?.includes(theme.id),
          )
          .map((theme) => theme.name) || []
      );
    }
  }, [combinedAssets, themes, selectedIndex]);

  let replaceModalOnConfirm = false;

  const handleConfirm = () => {
    replaceModalOnConfirm = true;
    if (uploadedAsset && selectedIndex !== null) {
      onChangeVariationAsset(
        uploadedAsset,
        selectedIndex,
        filteredSelectedTheme,
      );
      replaceAssetModal.onCloseModal();
      setSelectedIndex(null);
      setUploadedAsset(null);
    }
  };

  const handleCloseModal = () => {
    if (!replaceModalOnConfirm) {
      setSelectedIndex(null);
    }
    replaceAssetModal.onCloseModal();
  };

  const handleValidateSelectAsset = useCallback(
    (file: IAsset | File) => {
      const fileName = file instanceof File ? file.name : file.assetName;

      if (combinedAssets.some((asset) => asset?.assetName === fileName)) {
        Snackbar.warning(
          lang.productVariationValidateSelectAssetFailed(fileName),
        );

        return false;
      }

      return true;
    },
    [combinedAssets],
  );

  return (
    <Container>
      <Typography variant="h4" fontWeight="400">
        {lang.variationImages}
      </Typography>
      <Typography sx={{ mt: 1 }} fontWeight="300">
        {lang.themesAccessibility}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} mb={2}>
          {hasAssets && (
            <VariationAssetsContainer>
              <VariationAssetsHeader
                container
                alignItems="center"
                px={2}
                py={1}>
                <Grid item width="8%">
                  <DyHeaderContainer>
                    <Typography fontWeight={700}>{lang.dy}</Typography>
                    <VariationImagesTooltip
                      title={lang.variationImagesDyTooltip}
                      placement="top">
                      <IconButton size="small" disableRipple>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </VariationImagesTooltip>
                  </DyHeaderContainer>
                </Grid>

                <Grid item width="26%">
                  <Typography fontWeight={700}>{lang.asset}</Typography>
                </Grid>

                <Grid item flex={1}>
                  <Typography fontWeight={700}>{lang.theme}</Typography>
                </Grid>
              </VariationAssetsHeader>
              {combinedAssets?.map((item: any, index) => (
                <VariationAssetsItemWrapper key={index}>
                  <VariationAssetList
                    themes={themes || []}
                    index={index}
                    onChangeVariationTheme={onChangeVariationTheme}
                    deleteVariationAsset={deleteVariationAsset}
                    asset={item}
                    assetUploadModal={assetUploadModal}
                    onSelectIndex={setSelectedIndex}
                    groupProductAsset={groupProductAsset}
                    onDYChange={onChangeVariationDY}
                  />
                </VariationAssetsItemWrapper>
              ))}
            </VariationAssetsContainer>
          )}
        </Grid>

        <AssetUploadModal
          assetUploadModal={assetUploadModal}
          allowedFileExtension={ALLOWED_FILE_EXTENSIONS_PRODUCT}
          supportedFileType={ASSET_UPLOAD_SUPPORTED_FILE_TYPES_PRODUCT}
          onSelectAsset={handleAssetSelection}
          setSelectedIndex={setSelectedIndex}
          validateFile={handleValidateSelectAsset}
        />

        {!isViewOnly && (
          <Button
            variant="text"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => {
              assetUploadModal.onOpenModal();
            }}>
            {lang.uploadImage}
          </Button>
        )}
      </Grid>

      <SimpleDialog
        open={replaceAssetModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.replaceAssetModal.title}
        onClose={handleCloseModal}
        onClickCancel={handleCloseModal}
        subTitle={lang.replaceAssetModal.subTitle}
        modalSize="sm"
        onClickConfirm={handleConfirm}
      />
    </Container>
  );
};
